/**
 * DegreeCalculationService
 * 
 * Unified service for degree progress calculation used across all pages.
 * Consolidates logic from degree-search, degree-planning, and info-rutgers.
 * 
 * This service provides a consistent interface for calculating degree progress
 * and requirement progress across the application.
 */

import {
  Degree,
  Requirement,
  RequirementProgress,
  DegreeProgress,
  UserPrerequisiteInfo
} from '@/app/types/interfaces';
import { DegreeRequirementService, normalize } from './degreeRequirementService';
import { calculateOptimalCourseSet } from './degreeOptimization';

export interface DegreeCalculationContext {
  userPrerequisites: UserPrerequisiteInfo[];
  plannedCodes?: string[];
  plannedCourseListBlocks?: string[];
  expandedPools: Map<string, string[]>;
  courseListCache?: Map<number, { name: string; classes: string[] }>;
  creditsMap: Map<string, number>;
  degreeCourseListsDirect?: Map<string, { classes: string[]; name?: string; description?: string }>;
  courseNameMap?: Map<string, string>;
  catalogCourses?: Array<{ baseId?: string; id?: string; credits?: string | number; courseName?: string; name?: string }>;
}

export interface CalculateDegreeProgressOptions {
  includeMinimumCourses?: boolean;
  poolsOverride?: Map<string, string[]>;
}

/**
 * Expand course pools from degree requirements using degreeCourseListsDirect
 */
export function expandCoursePools(
  requirements: any,
  degreeCourseListsDirect: Map<string, { classes: string[]; name?: string; description?: string }>
): Map<string, string[]> {
  const expandedPools = new Map<string, string[]>();

  const collectEntries = (reqs: any[], accNames: Set<string>) => {
    if (!reqs || !Array.isArray(reqs)) return;
    reqs.forEach(r => {
      if (Array.isArray(r.course_pool)) {
        r.course_pool.forEach((e: string) => {
          if (degreeCourseListsDirect.has(e)) {
            accNames.add(e);
          }
        });
      }
      if (Array.isArray(r.requirements)) {
        collectEntries(r.requirements, accNames);
      }
    });
  };

  const namesToLoad = new Set<string>();
  const reqArray = Array.isArray(requirements) 
    ? requirements 
    : (requirements?.requirements || []);
  collectEntries(reqArray, namesToLoad);

  for (const name of namesToLoad) {
    const data = degreeCourseListsDirect.get(name);
    if (data && data.classes) {
      expandedPools.set(name, data.classes);
    }
  }

  return expandedPools;
}

/**
 * Calculate progress for a single requirement
 */
export function calculateRequirementProgress(
  requirement: Requirement,
  context: DegreeCalculationContext
): RequirementProgress {
  if (requirement.requirements?.length) {
    return DegreeRequirementService.aggregateRequirementProgress(
      requirement,
      requirement.requirements.map(child => calculateRequirementProgress(child, context)),
    );
  }
  return DegreeRequirementService.calculateRequirementProgress(requirement, {
    userPrerequisites: context.userPrerequisites,
    plannedCodes: context.plannedCodes || [],
    plannedCourseListBlocks: context.plannedCourseListBlocks || [],
    expandedPools: context.expandedPools,
    courseListCache: context.courseListCache || new Map(),
    creditsMap: context.creditsMap,
    degreeMappings: [] // No longer using ID-based mappings
  });
}

/**
 * Calculate overall degree progress
 * 
 * This is the main function used across all pages. It provides a consistent
 * interface for calculating degree progress with optional enhancements like
 * minimum courses calculation.
 */
export function calculateDegreeProgress(
  degree: Degree,
  requirements: any,
  context: DegreeCalculationContext,
  options: CalculateDegreeProgressOptions = {}
): DegreeProgress {
  const poolsToUse = options.poolsOverride || context.expandedPools;

  // Call the core service
  const res = DegreeRequirementService.calculateDegreeProgress(
    { ...degree, requirements },
    {
      userPrerequisites: context.userPrerequisites,
      plannedCodes: context.plannedCodes || [],
      plannedCourseListBlocks: context.plannedCourseListBlocks || [],
      expandedPools: poolsToUse,
      courseListCache: context.courseListCache || new Map(),
      creditsMap: context.creditsMap,
      degreeMappings: [] // No longer using ID-based mappings
    }
  );

  // Add minimum courses calculation if requested (used in degree-search)
  // This matches the logic from degree-search page
  let minimumCoursesNeeded: number | undefined = undefined;
  let minimumCourseListsNeeded: number | undefined = undefined;
  let minimumCoursesList: Array<{ course: string; name?: string; credits: number; requirements: string[] }> = [];
  let minimumCourseListsList: Array<{ listName: string; courses: string[]; requirements: string[] }> = [];

  if (options.includeMinimumCourses) {
    try {
      // Get all available courses for optimization
      const allCourses = context.catalogCourses ?? [];
      if (allCourses && allCourses.length > 0) {
        const courseUniverse = new Map<string, { credits: number; name?: string }>();
        
        allCourses.forEach((course: any) => {
          const code = course.baseId || course.id || '';
          if (code) {
            const normalized = normalize(code);
            const credits = typeof course.credits === 'string' 
              ? parseFloat(course.credits) 
              : Number(course.credits) || 3;
            
            if (!courseUniverse.has(normalized) || (course.courseName || course.name)) {
              courseUniverse.set(normalized, {
                credits: Number.isFinite(credits) && credits > 0 ? credits : 3,
                name: course.courseName || course.name || context.courseNameMap?.get(normalized)
              });
            }
          }
        });

        // Get completed courses (normalized)
        const completedCourseCodes = res.completedCourses.map(c => normalize(c));

        // Get requirements array
        const requirementsArray = Array.isArray(requirements) 
          ? requirements 
          : (requirements?.requirements || []);

        if (requirementsArray.length > 0 && courseUniverse.size > 0) {
          // Calculate optimal course set (matching degree-search logic)
          const optimalResult = calculateOptimalCourseSet(
            requirementsArray,
            completedCourseCodes,
            poolsToUse,
            courseUniverse,
            normalize
          );

          minimumCoursesNeeded = optimalResult.courses.length;
          minimumCoursesList = optimalResult.courses.map(courseCode => {
            const normalizedCode = normalize(courseCode);
            const courseInfo = courseUniverse.get(normalizedCode);
            const requirementsSatisfied: string[] = [];
            
            // Find which requirements this course satisfies
            optimalResult.requirementSatisfaction.forEach((satisfaction, reqId) => {
              if (satisfaction.coursesUsed.some(c => normalize(c) === normalizedCode)) {
                requirementsSatisfied.push(reqId);
              }
            });

            return {
              course: courseCode,
              name: courseInfo?.name || context.courseNameMap?.get(normalizedCode),
              credits: courseInfo?.credits || 3,
              requirements: requirementsSatisfied
            };
          });

          // Calculate minimum course lists needed
          const courseListUsage = new Map<string, { requirements: string[]; courses: string[] }>();
          
          const collectCourseListRefs = (reqs: any[], acc: Set<string>) => {
            reqs.forEach(req => {
              if (Array.isArray(req.course_pool)) {
                req.course_pool.forEach((entry: string) => {
                  if (poolsToUse.has(entry)) {
                    acc.add(entry);
                  }
                });
              }
              if (Array.isArray(req.requirements)) {
                collectCourseListRefs(req.requirements, acc);
              }
            });
          };

          const courseListRefs = new Set<string>();
          collectCourseListRefs(requirementsArray, courseListRefs);

          for (const listName of courseListRefs) {
            const listCourses = poolsToUse.get(listName) || [];
            const listCoursesSet = new Set(listCourses.map(normalize));
            
            const coursesFromList = optimalResult.courses.filter(c => 
              listCoursesSet.has(normalize(c))
            );

            if (coursesFromList.length > 0) {
              const requirementsForList: string[] = [];
              optimalResult.requirementSatisfaction.forEach((satisfaction, reqId) => {
                const hasListCourse = satisfaction.coursesUsed.some(c => 
                  listCoursesSet.has(normalize(c))
                );
                if (hasListCourse) {
                  requirementsForList.push(reqId);
                }
              });

              if (requirementsForList.length > 0) {
                courseListUsage.set(listName, {
                  requirements: requirementsForList,
                  courses: listCourses
                });
              }
            }
          }

          minimumCourseListsNeeded = courseListUsage.size;
          minimumCourseListsList = Array.from(courseListUsage.entries()).map(([listName, data]) => ({
            listName,
            courses: data.courses,
            requirements: data.requirements
          }));
        }
      }
    } catch (error) {
      console.error('[calculateDegreeProgress] Error calculating minimum courses:', error);
    }
  }

  // Map the result to DegreeProgress interface
  return {
    degreeId: degree.ID,
    degreeName: degree.Name,
    overallProgress: res.percentComplete,
    requirementProgress: res.requirementProgress,
    creditProgress: res.creditProgress,
    requirementsProgress: res.requirementsProgress,
    completedCourses: res.completedCourses,
    remainingCourses: res.remainingCourses,
    estimatedCompletion: 'TBD', // Can be calculated if needed
    totalCreditsNeeded: res.totalCreditsNeeded,
    totalCreditsCompleted: res.totalCreditsCompleted,
    totalCreditsPlanned: res.totalCreditsPlanned,
    minimumCoursesNeeded,
    minimumCourseListsNeeded,
    minimumCoursesList,
    minimumCourseListsList
  };
}

/**
 * Helper to create a context object from common page state
 */
export function createCalculationContext(
  userPrerequisites: UserPrerequisiteInfo[],
  expandedPools: Map<string, string[]>,
  creditsMap: Map<string, number>,
  options: {
    plannedCodes?: string[];
    plannedCourseListBlocks?: string[];
    courseListCache?: Map<number, { name: string; classes: string[] }>;
    degreeCourseListsDirect?: Map<string, { classes: string[]; name?: string; description?: string }>;
    courseNameMap?: Map<string, string>;
    catalogCourses?: DegreeCalculationContext['catalogCourses'];
  } = {}
): DegreeCalculationContext {
  return {
    userPrerequisites,
    plannedCodes: options.plannedCodes || [],
    plannedCourseListBlocks: options.plannedCourseListBlocks || [],
    expandedPools,
    courseListCache: options.courseListCache || new Map(),
    creditsMap,
    degreeCourseListsDirect: options.degreeCourseListsDirect,
    courseNameMap: options.courseNameMap,
    catalogCourses: options.catalogCourses,
  };
}
